import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { Upload, FileText, FileSpreadsheet, Image, X, CheckCircle, AlertCircle, Globe, Settings, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { detectLanguageFromText } from '@/utils/languageDetection';
import { useUserPreferences } from '@/hooks/useUserPreferences';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { useTranslation } from 'react-i18next';
import { LanguageDetectionBadge } from '@/components/LanguageDetectionBadge';

interface DocumentImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImportComplete: () => void;
}

interface UploadFile {
  file: File;
  id: string;
  status: 'pending' | 'uploading' | 'processing' | 'completed' | 'error';
  progress: number;
  error?: string;
  templateName?: string;
  detectedLanguage?: string;
  languageConfidence?: number;
  preview?: {
    itemCount?: number;
    categories?: string[];
    detectedType?: string;
  };
}

interface LanguageDetectionState {
  detectedLanguages: Record<string, { language: string; confidence: number }>;
  userLanguageOverride?: string;
}

const SUPPORTED_FORMATS = {
  'application/pdf': { icon: FileText, label: 'PDF Document', color: 'text-red-500' },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { icon: FileText, label: 'Word Document', color: 'text-blue-500' },
  'application/msword': { icon: FileText, label: 'Word Document', color: 'text-blue-500' },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { icon: FileSpreadsheet, label: 'Excel Spreadsheet', color: 'text-green-500' },
  'application/vnd.ms-excel': { icon: FileSpreadsheet, label: 'Excel Spreadsheet', color: 'text-green-500' },
  'text/csv': { icon: FileSpreadsheet, label: 'CSV File', color: 'text-green-500' },
  'image/jpeg': { icon: Image, label: 'JPEG Image', color: 'text-purple-500' },
  'image/jpg': { icon: Image, label: 'JPEG Image', color: 'text-purple-500' },
  'image/png': { icon: Image, label: 'PNG Image', color: 'text-purple-500' },
  'text/plain': { icon: FileText, label: 'Text File', color: 'text-gray-500' }
};

export const DocumentImportDialog = ({ open, onOpenChange, onImportComplete }: DocumentImportDialogProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation();
  const { preferences } = useUserPreferences();
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [languageDetection, setLanguageDetection] = useState<LanguageDetectionState>({
    detectedLanguages: {},
    userLanguageOverride: preferences?.language_code || 'en'
  });

  const generateFileId = () => Math.random().toString(36).substring(2, 15);

  const getFileIcon = (mimeType: string) => {
    const format = SUPPORTED_FORMATS[mimeType as keyof typeof SUPPORTED_FORMATS];
    return format?.icon || FileText;
  };

  const getFileFormat = (mimeType: string) => {
    return SUPPORTED_FORMATS[mimeType as keyof typeof SUPPORTED_FORMATS];
  };

  const isFileSupported = (file: File) => {
    return Object.keys(SUPPORTED_FORMATS).includes(file.type);
  };

  const sanitizeFileName = (fileName: string) => {
    const lastDotIndex = fileName.lastIndexOf('.');
    const name = lastDotIndex !== -1 ? fileName.substring(0, lastDotIndex) : fileName;
    const extension = lastDotIndex !== -1 ? fileName.substring(lastDotIndex) : '';
    
    let sanitized = name
      .replace(/[æÆ]/g, 'ae')
      .replace(/[øØ]/g, 'o')
      .replace(/[åÅ]/g, 'a')
      .replace(/[àáâãäåÀÁÂÃÄÅ]/g, 'a')
      .replace(/[èéêëÈÉÊË]/g, 'e')
      .replace(/[ìíîïÌÍÎÏ]/g, 'i')
      .replace(/[òóôõöÒÓÔÕÖ]/g, 'o')
      .replace(/[ùúûüÙÚÛÜ]/g, 'u')
      .replace(/[ýÿÝŸ]/g, 'y')
      .replace(/[ñÑ]/g, 'n')
      .replace(/[çÇ]/g, 'c')
      .replace(/[\s\-_]+/g, '-')
      .replace(/[^a-zA-Z0-9\-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
    
    return sanitized + extension;
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    addFiles(droppedFiles);
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      addFiles(selectedFiles);
    }
  };

  const addFiles = useCallback(async (newFiles: File[]) => {
    const validFiles = newFiles.filter(isFileSupported);
    
    if (validFiles.length !== newFiles.length) {
      toast({
        title: "Some files not supported",
        description: "Only PDF, Word, Excel, CSV, and image files are supported for document import.",
        variant: "destructive",
      });
    }

    const uploadFiles = validFiles.map(file => ({
      file,
      id: generateFileId(),
      status: 'pending' as const,
      progress: 0,
    }));

    setFiles(prev => [...prev, ...uploadFiles]);

    // Quick language detection for text files
    for (const uploadFile of uploadFiles) {
      if (uploadFile.file.type === 'text/csv' || uploadFile.file.type === 'text/plain') {
        try {
          const text = await uploadFile.file.text();
          const detection = detectLanguageFromText(text);
          
          setLanguageDetection(prev => ({
            ...prev,
            detectedLanguages: {
              ...prev.detectedLanguages,
              [uploadFile.id]: { language: detection.detectedLanguage, confidence: detection.confidence }
            }
          }));
          
          setFiles(prev => prev.map(f => 
            f.id === uploadFile.id 
              ? { 
                  ...f, 
                  detectedLanguage: detection.detectedLanguage, 
                  languageConfidence: detection.confidence,
                  preview: {
                    detectedType: 'structured_data',
                    itemCount: text.split('\n').length - 1
                  }
                }
              : f
          ));
        } catch (error) {
          console.error('Language detection failed:', error);
        }
      }
    }
  }, [toast]);

  const removeFile = (fileId: string) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
    setLanguageDetection(prev => ({
      ...prev,
      detectedLanguages: Object.fromEntries(
        Object.entries(prev.detectedLanguages).filter(([id]) => id !== fileId)
      )
    }));
  };

  const uploadFile = async (uploadFile: UploadFile) => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "You must be logged in to import documents.",
        variant: "destructive",
      });
      return;
    }

    try {
      setFiles(prev => prev.map(f => 
        f.id === uploadFile.id ? { ...f, status: 'uploading', progress: 25 } : f
      ));

      const timestamp = Date.now();
      const sanitizedName = sanitizeFileName(uploadFile.file.name);
      
      // Get tenant_id first for storage path
      const { data: tenantData } = await supabase
        .from('user_roles')
        .select('tenant_id')
        .eq('user_id', user.id)
        .single();

      if (!tenantData) throw new Error('No organization found for user');
      
      // Include tenant_id as first folder for RLS policy compliance
      const fileName = `${tenantData.tenant_id}/${timestamp}-${sanitizedName}`;

      // Upload to Supabase storage
      const { error: uploadError } = await supabase.storage
        .from('imported-documents')
        .upload(fileName, uploadFile.file);

      if (uploadError) throw uploadError;

      // Create import record with document import source
      const { data: importRecord, error: insertError } = await supabase
        .from('imported_templates')
        .insert({
          tenant_id: tenantData.tenant_id,
          sender_email: user.email!,
          original_filename: uploadFile.file.name,
          document_url: fileName,
          status: 'processing',
          import_source: 'document_import'
        })
        .select()
        .single();

      if (insertError) throw insertError;

      setFiles(prev => prev.map(f => 
        f.id === uploadFile.id ? { ...f, progress: 75, status: 'processing' } : f
      ));

      // Process with enhanced language context
      const userLanguage = languageDetection.userLanguageOverride || preferences?.language_code || 'en';
      
      const { data: { session } } = await supabase.auth.getSession();
      const { data: functionData, error: functionError } = await supabase.functions.invoke('process-direct-upload', {
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: {
          importId: importRecord.id,
          fileType: uploadFile.file.type,
          fileName: sanitizedName,
          userLanguage,
          userTimezone: preferences?.time_zone || 'UTC',
          importSource: 'document_import', // Distinguish from email import
          importType: 'document_import' // Legacy compatibility
        }
      });

      if (functionError || (functionData && functionData.success === false)) {
        const errorMessage = functionData?.error || functionError?.message || 'Document processing failed';
        throw new Error(errorMessage);
      }

      setFiles(prev => prev.map(f => 
        f.id === uploadFile.id ? { 
          ...f, 
          status: 'completed', 
          progress: 100,
          templateName: functionData?.templateName || `Template from ${uploadFile.file.name}`
        } : f
      ));

    } catch (error: any) {
      console.error('Document import failed:', error);
      const errorMessage = error.message || 'Import failed';
      
      setFiles(prev => prev.map(f => 
        f.id === uploadFile.id ? { 
          ...f, 
          status: 'error', 
          error: errorMessage,
          progress: 0
        } : f
      ));
    }
  };

  const handleImportAll = async () => {
    const pendingFiles = files.filter(f => f.status === 'pending');
    
    for (const file of pendingFiles) {
      await uploadFile(file);
    }

    const completedFiles = files.filter(f => f.status === 'completed');
    const errorFiles = files.filter(f => f.status === 'error');
    
    if (completedFiles.length > 0 && errorFiles.length === 0) {
      toast({
        title: "Import complete",
        description: `Successfully processed ${completedFiles.length} document(s). Check the Templates page to review and approve them.`,
      });
    } else if (completedFiles.length > 0 && errorFiles.length > 0) {
      toast({
        title: "Import partially complete",
        description: `${completedFiles.length} succeeded, ${errorFiles.length} failed`,
        variant: "destructive",
      });
    } else if (errorFiles.length > 0) {
      toast({
        title: "Import failed",
        description: `Failed to process ${errorFiles.length} document(s)`,
        variant: "destructive",
      });
    }

    onImportComplete();
  };

  const getStatusIcon = (status: UploadFile['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return null;
    }
  };

  // Update language override when preferences change
  useEffect(() => {
    if (preferences?.language_code && !languageDetection.userLanguageOverride) {
      setLanguageDetection(prev => ({
        ...prev,
        userLanguageOverride: preferences.language_code
      }));
    }
  }, [preferences?.language_code]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Import Documents
          </DialogTitle>
          <DialogDescription>
            Convert your existing inspection forms, checklists, and HACCP documents into structured templates. Supports multiple languages and preserves all original items.
          </DialogDescription>
        </DialogHeader>

        <div
          className={cn(
            "border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center transition-colors",
            isDragging && "border-primary bg-primary/5",
            files.length > 0 && "border-solid border-border"
          )}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {files.length === 0 ? (
            <div className="space-y-4">
              <Upload className="h-12 w-12 mx-auto text-muted-foreground" aria-hidden="true" />
              <div>
                <p className="text-lg font-medium">Drop your documents here or click to browse</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Supports inspection forms, checklists, and HACCP documents in PDF, Word, Excel, CSV, and image formats
                </p>
              </div>
              <input
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.jpg,.jpeg,.png"
                onChange={handleFileSelect}
                className="sr-only"
                id="document-upload"
              />
              <label
                htmlFor="document-upload"
                className="inline-flex items-center px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 cursor-pointer"
              >
                Browse Documents
              </label>
            </div>
          ) : (
            <div className="space-y-3">
              {files.map((file) => {
                const Icon = getFileIcon(file.file.type);
                const format = getFileFormat(file.file.type);
                const statusIcon = getStatusIcon(file.status);
                
                return (
                  <div key={file.id} className="flex items-center gap-3 p-3 border rounded-lg">
                    <div className="flex items-center gap-2 flex-1">
                      <Icon className={`h-4 w-4 ${format?.color || 'text-gray-500'}`} />
                      <div className="flex flex-col flex-1">
                        <span className="text-sm font-medium">{file.file.name}</span>
                        <div className="flex items-center gap-2 mt-1">
                          {file.detectedLanguage && (
                            <LanguageDetectionBadge 
                              language={file.detectedLanguage}
                              confidence={file.languageConfidence}
                            />
                          )}
                          {file.preview && (
                            <Badge variant="outline" className="text-xs">
                              {file.preview.itemCount ? `${file.preview.itemCount} items` : 'Processing...'}
                            </Badge>
                          )}
                        </div>
                        {file.status === 'processing' && (
                          <div className="mt-2">
                            <Progress value={file.progress} className="h-2" />
                            <p className="text-xs text-muted-foreground mt-1">
                              Analyzing document structure and preserving all items...
                            </p>
                          </div>
                        )}
                        {file.error && (
                          <p className="text-xs text-red-500 mt-1">{file.error}</p>
                        )}
                        {file.templateName && file.status === 'completed' && (
                          <div className="mt-1">
                            <p className="text-xs text-green-600">{file.templateName}</p>
                            <p className="text-xs text-muted-foreground">Review and approve on Templates page</p>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {statusIcon}
                      {file.status === 'pending' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFile(file.id)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
              
              <input
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.jpg,.jpeg,.png"
                onChange={handleFileSelect}
                className="sr-only"
                id="additional-upload"
              />
              <label
                htmlFor="additional-upload"
                className="inline-flex items-center px-3 py-2 text-sm border border-dashed border-muted-foreground/50 rounded-md hover:bg-muted/50 cursor-pointer"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add More Documents
              </label>
            </div>
          )}
        </div>

        {files.length > 0 && (
          <div className="border-t pt-4">
            <div className="flex items-center gap-2 mb-3">
              <Settings className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Processing Settings</span>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="processing-language" className="text-sm font-medium">
                  Processing Language
                </Label>
                <Select
                  value={languageDetection.userLanguageOverride}
                  onValueChange={(value) => 
                    setLanguageDetection(prev => ({ ...prev, userLanguageOverride: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="no">Norwegian</SelectItem>
                    <SelectItem value="sv">Swedish</SelectItem>
                    <SelectItem value="da">Danish</SelectItem>
                    <SelectItem value="fi">Finnish</SelectItem>
                    <SelectItem value="de">German</SelectItem>
                    <SelectItem value="fr">French</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleImportAll}
            disabled={files.length === 0 || files.every(f => f.status !== 'pending')}
          >
            Import Documents
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};